/**
 * Task #1527 — Hook FE per snapshot quota R2 + DB del tenant corrente.
 * Promosso da `packages/frontend/src/hooks/useStorageQuota.ts` a ui-frontend.
 *
 * Pattern listener-driven: il backend espone gli header `X-Storage-Quota-*`
 * su ogni response → l'apiClient di ciascun frontend dispatcha un
 * `STORAGE_QUOTA_HEADER_EVENT` (CustomEvent passivo). Questo hook ascolta
 * l'evento e aggiorna lo snapshot localmente. Una sola fetch "deep" iniziale
 * a `/storage/quota/me` (passata via prop `fetchSnapshot` per disaccoppiare
 * dall'apiClient specifico del frontend) per popolare i campi non in header.
 */
import { useEffect, useState, useCallback, useRef } from 'react';
import type {
  QuotaCategory,
  QuotaStatus,
  StorageQuotaSnapshot,
} from '@easyflow/shared';

export type { QuotaCategory, QuotaStatus, StorageQuotaSnapshot } from '@easyflow/shared';
export type { QuotaScopeSnapshot, QuotaScope, QuotaThreshold } from '@easyflow/shared';

/** Hint estratto dagli header HTTP (sottoinsieme dello snapshot). */
export interface StorageQuotaHeaderHint {
  r2Pct: number | null;
  r2Status: string | null;
  dbPct: number | null;
  dbStatus: string | null;
  blockedCategories: string[];
  emergencyUntil: string | null;
  ts: number;
}

export const STORAGE_QUOTA_HEADER_EVENT = 'edgvoip:storage-quota-header';

const DEEP_REFRESH_MIN_INTERVAL_MS = 5 * 60_000;

function isQuotaStatus(s: string | null): s is QuotaStatus {
  return s === 'ok' || s === 'info' || s === 'warning' || s === 'critical' || s === 'grace' || s === 'blocked';
}

function isQuotaCategory(c: string): c is QuotaCategory {
  return c === 'embedding' || c === 'upload' || c === 'export';
}

function applyHint(prev: StorageQuotaSnapshot | null, hint: StorageQuotaHeaderHint): StorageQuotaSnapshot | null {
  if (!prev) return prev;
  const next: StorageQuotaSnapshot = {
    ...prev,
    r2: { ...prev.r2 },
    db: prev.db ? { ...prev.db } : null,
  };
  if (hint.r2Pct !== null) next.r2.pct = hint.r2Pct;
  if (isQuotaStatus(hint.r2Status)) next.r2.status = hint.r2Status;
  if (next.db && hint.dbPct !== null) next.db.pct = hint.dbPct;
  if (next.db && isQuotaStatus(hint.dbStatus)) next.db.status = hint.dbStatus;
  next.blockedCategories = hint.blockedCategories.filter(isQuotaCategory);
  next.emergencyUnblockUntil = hint.emergencyUntil;
  return next;
}

export interface UseStorageQuotaOptions {
  /** Fetcher iniettato dal frontend (evita dipendenza da uno specifico apiClient). */
  fetchSnapshot: () => Promise<StorageQuotaSnapshot | null>;
}

export interface UseStorageQuotaResult {
  snapshot: StorageQuotaSnapshot | null;
  loading: boolean;
  refresh: () => Promise<void>;
}

export function useStorageQuota(options: UseStorageQuotaOptions): UseStorageQuotaResult {
  const { fetchSnapshot } = options;
  const [snapshot, setSnapshot] = useState<StorageQuotaSnapshot | null>(null);
  const [loading, setLoading] = useState(true);
  const lastDeepFetchRef = useRef<number>(0);
  const lastStatusRef = useRef<string>('');
  // I call-site passano quasi sempre una arrow function inline: se `refresh`
  // dipendesse direttamente da `fetchSnapshot`, ogni re-render del parent
  // (es. Header con orologio a 1s) rilancerebbe l'effetto iniziale → una
  // fetch `/storage/quota/me` al secondo all'infinito. Il ref disaccoppia
  // l'identità del fetcher dalla stabilità di `refresh`.
  const fetchSnapshotRef = useRef(fetchSnapshot);
  useEffect(() => {
    fetchSnapshotRef.current = fetchSnapshot;
  }, [fetchSnapshot]);

  const refresh = useCallback(async () => {
    try {
      lastDeepFetchRef.current = Date.now();
      const snap = await fetchSnapshotRef.current();
      if (snap) {
        setSnapshot(snap);
        lastStatusRef.current = `${snap.r2.status}|${snap.db?.status || ''}`;
      }
    } catch {
      // best-effort: silenzioso
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useEffect(() => {
    function onHint(e: Event): void {
      const ce = e as CustomEvent<StorageQuotaHeaderHint>;
      const hint = ce.detail;
      if (!hint) return;
      setSnapshot((prev) => applyHint(prev, hint));
      const newStatusKey = `${hint.r2Status || ''}|${hint.dbStatus || ''}`;
      const statusChanged = newStatusKey !== lastStatusRef.current && newStatusKey !== '|';
      const stale = Date.now() - lastDeepFetchRef.current > DEEP_REFRESH_MIN_INTERVAL_MS;
      if (statusChanged || stale) {
        lastStatusRef.current = newStatusKey;
        void refresh();
      }
    }
    window.addEventListener(STORAGE_QUOTA_HEADER_EVENT, onHint);
    return () => window.removeEventListener(STORAGE_QUOTA_HEADER_EVENT, onHint);
  }, [refresh]);

  return { snapshot, loading, refresh };
}

export function severityForStatus(status: QuotaStatus): 'ok' | 'info' | 'warning' | 'critical' {
  if (status === 'blocked' || status === 'grace') return 'critical';
  if (status === 'critical' || status === 'warning') return 'warning';
  if (status === 'info') return 'info';
  return 'ok';
}

export function shouldShowBanner(snap: StorageQuotaSnapshot | null): boolean {
  if (!snap) return false;
  if (snap.r2.status === 'blocked' || snap.r2.status === 'grace' || snap.r2.status === 'critical' || snap.r2.status === 'warning') return true;
  if (snap.db && (snap.db.status === 'blocked' || snap.db.status === 'grace' || snap.db.status === 'critical' || snap.db.status === 'warning')) return true;
  return false;
}

export function formatBytes(b: number): string {
  if (!b) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(b) / Math.log(k));
  return `${parseFloat((b / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
}
